import { useState } from 'react';
import { Trash2, RefreshCw, Clock, ArrowLeft, UserX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Badge,
  Card,
  PageHeader,
} from '@license-management/ui';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useToast } from '../components/Toast';
import {
  useDeletedCustomerUsers,
  useRestoreCustomerUser,
  usePermanentlyDeleteCustomerUser,
  DeletedCustomerUser,
} from '../api/hooks/customerUsers';

export function CustomerUsersTrash() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [selectedUser, setSelectedUser] = useState<DeletedCustomerUser | null>(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showPermanentDeleteDialog, setShowPermanentDeleteDialog] = useState(false);

  // Fetch deleted users
  const { data: deletedUsers, isLoading, error } = useDeletedCustomerUsers();

  // Restore mutation
  const restoreMutation = useRestoreCustomerUser();

  // Permanent delete mutation
  const permanentDeleteMutation = usePermanentlyDeleteCustomerUser();

  const handleRestore = async () => {
    if (!selectedUser) return;
    try {
      await restoreMutation.mutateAsync(selectedUser.id);
      setShowRestoreDialog(false);
      setSelectedUser(null);
      showToast('Customer user restored successfully', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to restore user', 'error');
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedUser) return;
    try {
      await permanentDeleteMutation.mutateAsync(selectedUser.id);
      setShowPermanentDeleteDialog(false);
      setSelectedUser(null);
      showToast('Customer user permanently deleted', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to delete user', 'error');
    }
  };

  const getDaysUntilDeletionVariant = (days: number): 'error' | 'warning' | 'default' => {
    if (days <= 7) return 'error';
    if (days <= 14) return 'warning';
    return 'default';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message="Failed to load deleted customer users" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Portal User Recycle Bin"
        description="Deleted portal users are kept for 30 days before permanent deletion"
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate('/customer-users')}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to Portal Users
          </Button>
        }
      />

      {/* Users table */}
      {!deletedUsers || deletedUsers.length === 0 ? (
        <Card className="text-center py-12">
          <UserX className="mx-auto h-12 w-12 text-zinc-400 mb-4" />
          <h3 className="text-lg font-medium text-zinc-900 mb-2">No deleted users</h3>
          <p className="text-zinc-500">The recycle bin is empty.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-200">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Deleted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Time Until Deletion
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-zinc-200">
                {deletedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-zinc-900">
                          {user.name || 'No name'}
                        </div>
                        <div className="text-sm text-zinc-500">
                          {user.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-zinc-900">
                        {user.customer?.name || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-zinc-900">
                          {new Date(user.deletedAt).toLocaleDateString()}
                        </div>
                        <div className="text-sm text-zinc-500">
                          by {user.deletedByUser?.email || user.deletedByUser?.name || 'Unknown'}
                        </div>
                        {user.deletionReason && (
                          <div className="text-sm text-zinc-400 italic">
                            "{user.deletionReason}"
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={getDaysUntilDeletionVariant(user.daysUntilPermanentDeletion)}>
                        <Clock className="h-3 w-3 mr-1" />
                        {user.daysUntilPermanentDeletion} days
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowRestoreDialog(true);
                          }}
                          leftIcon={<RefreshCw className="h-4 w-4" />}
                        >
                          Restore
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowPermanentDeleteDialog(true);
                          }}
                          leftIcon={<Trash2 className="h-4 w-4" />}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Restore Dialog */}
      <ConfirmDialog
        isOpen={showRestoreDialog}
        onCancel={() => {
          setShowRestoreDialog(false);
          setSelectedUser(null);
        }}
        onConfirm={handleRestore}
        title="Restore Customer User"
        message={`Are you sure you want to restore ${selectedUser?.name || selectedUser?.email}?`}
        confirmLabel="Restore"
      />

      {/* Permanent Delete Dialog */}
      <ConfirmDialog
        isOpen={showPermanentDeleteDialog}
        onCancel={() => {
          setShowPermanentDeleteDialog(false);
          setSelectedUser(null);
        }}
        onConfirm={handlePermanentDelete}
        title="Permanently Delete Customer User"
        message={`This action cannot be undone! Are you sure you want to permanently delete ${selectedUser?.name || selectedUser?.email}? All associated data will be removed.`}
        confirmLabel="Permanently Delete"
        severity="high"
      />
    </div>
  );
}

export default CustomerUsersTrash;
